import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { track } from '../lib/analytics';
import { CheckCircle, Loader2, Clock, RefreshCw, Hourglass } from 'lucide-react';
import { useTrial } from '../contexts/TrialContext';

const POLL_ATTEMPTS = 8;
const POLL_MS = 2500;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Return page after Stripe (embedded or hosted). Confirms the payment with our
 * API, then falls back to a Stripe reconcile (by session + by e-mail) so a
 * charged student is never left on the free plan or sent back to pay again.
 */
export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { user, setUser } = useAuth();
  const { cfg: trialCfg, hours: trialHours } = useTrial();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [round, setRound] = useState(0);
  const [paidPlan, setPaidPlan] = useState('pro');
  const trialWasActive = React.useRef(user?.trial?.status === 'active');

  const markSuccess = useCallback(async (data) => {
    localStorage.removeItem('pro_checkout_pending');
    setPaidPlan(data?.plan_id === 'trial' ? 'trial' : 'pro');
    track('purchase', {
      value: (data?.amount_total || 0) / 100,
      currency: (data?.currency || 'cad').toLowerCase(),
      transaction_id: sessionId || data?.session_id || '',
      plan_id: data?.plan_id || 'pro',
    });
    try {
      const meRes = await api.get('/api/auth/me');
      localStorage.setItem('user', JSON.stringify(meRes.data));
      setUser(meRes.data);
    } catch { /* user refresh is best-effort */ }
    setStatus('success');
  }, [sessionId, setUser]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setStatus('checking');
      if (sessionId) {
        for (let i = 0; i < POLL_ATTEMPTS && alive; i += 1) {
          try {
            const { data } = await api.get(`/api/payments/status/${sessionId}`);
            if (data.payment_status === 'paid') { if (alive) await markSuccess(data); return; }
            if (data.status === 'expired') break;
          } catch (e) {
            if (e?.response?.status === 404) break; // record missing → go straight to reconcile
          }
          await sleep(POLL_MS);
        }
      }
      // Recovery net: reconcile with Stripe (by our records, then by e-mail).
      for (let i = 0; i < 2 && alive; i += 1) {
        try {
          const { data } = await api.post('/api/payments/reconcile');
          if (data?.subscription_status && data.subscription_status !== 'free') { if (alive) await markSuccess({ plan_id: data.subscription_status }); return; }
          if (data?.trial?.status === 'active' && !trialWasActive.current) { if (alive) await markSuccess({ plan_id: 'trial' }); return; }
        } catch { /* try again below */ }
        await sleep(POLL_MS);
      }
      if (alive) setStatus('pending');
    })();
    return () => { alive = false; };
  }, [sessionId, round, markSuccess]);

  const isTrial = paidPlan === 'trial';
  const dest = user?.onboarding_completed === false ? '/onboarding' : (isTrial ? '/dashboard' : '/start-here');
  useEffect(() => {
    if (status !== 'success') return undefined;
    const t = setTimeout(() => navigate(dest), 2500);
    return () => clearTimeout(t);
  }, [status, navigate, dest]);

  return (
    <DashboardLayout>
      <div className="max-w-md mx-auto text-center py-16" data-testid="subscription-page">
        {status === 'checking' && (
          <div data-testid="subscription-checking">
            <Loader2 size={48} className="animate-spin text-primary mx-auto mb-4" />
            <h1 className="text-xl font-bold text-neutral-900 mb-2">Confirming your payment…</h1>
            <p className="text-neutral-500 text-sm">This usually takes a few seconds. Please don't close this page.</p>
          </div>
        )}
        {status === 'success' && (
          <div data-testid="subscription-success" data-plan={paidPlan}>
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${isTrial ? 'bg-amber-100' : 'bg-green-100'}`}>
              {isTrial ? <Hourglass size={40} className="text-amber-600" /> : <CheckCircle size={40} className="text-green-600" />}
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-2" data-testid="subscription-success-title">{isTrial ? (trialCfg.success_title || 'Your trial has started!') : 'Welcome to Pro!'}</h1>
            <p className="text-neutral-600 mb-6">{isTrial ? (trialCfg.success_text || `You have ${trialHours} hours of access. Taking you to setup…`) : 'Your Pro pass is active. Taking you to setup…'}</p>
            <div className="flex gap-3 justify-center">
              <Link to={dest} className="px-6 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary-700 transition" data-testid="go-dashboard">Continue Setup</Link>
            </div>
          </div>
        )}
        {status === 'pending' && (
          <div data-testid="subscription-pending">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Clock size={40} className="text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-neutral-900 mb-2">We're still confirming your payment</h1>
            <p className="text-neutral-600 mb-2 text-sm">Stripe sometimes needs a minute to report a payment back to us. Your card is only ever charged once, so please <span className="font-semibold">don't pay again</span>.</p>
            <p className="text-neutral-500 mb-6 text-sm">If you were charged, your Pro access will be activated automatically — tap "Check again" in a moment, or message us and we'll sort it out right away.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button type="button" onClick={() => setRound((r) => r + 1)} className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary-700 transition" data-testid="subscription-check-again">
                <RefreshCw size={16} /> Check again
              </button>
              <Link to="/dashboard" className="px-6 py-2.5 border border-neutral-300 text-neutral-700 font-medium rounded-lg hover:bg-neutral-50 transition" data-testid="subscription-dashboard-link">Go to dashboard</Link>
            </div>
            <p className="text-xs text-neutral-400 mt-6">Didn't pay, or the card was declined? <Link to={user?.trial?.status === 'unpaid' ? '/checkout?plan=trial' : '/checkout'} className="underline" data-testid="subscription-back-to-checkout">Return to checkout</Link></p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
